import type {
  CommandData,
  Simulation as GeneralSimulation,
  ValidationOverride,
  VFrameBase,
} from '@festina/life-general-types/life'
import { isValidationError } from '@festina-life/api'
import {
  assertNotEmpty,
  type Loadable,
  notEmpty,
  type Optional,
  type Server,
  typeUtils,
} from '@festina-life/core'
import { Button } from '@festina-life/ui/actions'
import {
  sanitizeFormData,
  type SubmitStatus,
} from '@festina-life/ui/formikForms'
import { inputWrapperMinWidthStyles, RawSwitch } from '@festina-life/ui/forms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack } from '@festina-life/ui/structure'
import {
  type CommandActionVariantOrSimulation,
  DataProvider,
} from '@festina-life/ui/theme'
import cx from 'classnames'
import {
  type ComponentType,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import type { EventVersionWithCommandType } from '../../hooks/useFilterCommands'
import { ErrorMessage } from '../errorFallback/ErrorMessage'
import { LifeErrorFallback } from '../errorFallback/LifeErrorFallback'
import { shouldShowSimulationAndAllowFetchForFrame } from './CommandDataFormAndSimulation.utils'
import type { CommandSubmissionData } from './commandPalette/CommandPaletteModalButton'
import { CommandPaletteModalSpinner } from './commandPalette/CommandPaletteModalSpinner'
import { ExtraCommandData } from './ExtraCommandData'
import { Simulation } from './Simulation'
import type { AnyCommandVersion } from './types'
import { ValidationErrorMessageAlert } from './ValidationErrorMessageAlert'
import { ValidationOverridesTable } from './ValidationOverridesTable'

/**
 * Properties to spread over the inner form.
 */
export type CommandDataFormProps<
  T extends AnyCommandVersion['commandData'],
  E = string,
  F extends VFrameBase = VFrameBase,
> = {
  /**
   * Used as the `form` prop of the `type="submit"` button.
   * Without it, the form's own submit cannot be triggered.
   */
  formId: string
  /**
   * Used to display error alerts and show loading state.
   */
  onStatusChanged: (status: SubmitStatus) => void
  /**
   * Used to prevent the modal from closing if data would be lost.
   */
  onDirtyChange?: (dirty: boolean) => void
  /**
   * Used to reset and fetch the simulation.
   */
  onInputChange: (values: T) => void
  /**
   * Used for disabeling the simulation button when there are errors in the form.
   */
  onErrorChange?: (error: boolean) => void
  /**
   * Used to bubble-up the trigger to revalidate the form.
   */
  onSetRevalidateForm?: (onRevalidateForm: () => Promise<boolean>) => void
  /** Used to bubble-up Formik's submitForm for external flows. */
  onSetSubmitForm?: (submitForm: (() => Promise<unknown>) | undefined) => void
  onSubmit: CommandSubmissionData<NonNullable<T>>['onSubmit']
  shouldFieldsBeDisabled: boolean
  shouldSkipFrame: boolean | undefined
  commandData: Partial<T> // `Partial` because we don't have the `_type` when passing `validationOverrides`.
  commandActionVariant?: CommandActionVariantOrSimulation
  /**
   * When the command is mounted on a parent entity (like company)
   * and the child is not available in the URL params yet (like employer agreements),
   * this prop dictates the identifier for that child entity.
   **/
  entityId?: E
  isValidationDisabled?: boolean
  onSubmitError?: (error: Error | undefined) => void
  validationOverrides?: ValidationOverride[]
  /** Frame fetcher service method obtained from the command actions. */
  // Optional until CommandModalButton can guarantee to have it.
  fetchFrame?: (commandData: T) => Promise<F | null>
  onFrameIsLoadingChanged: (isLoading: boolean) => void
  onFormHasValidationChanged?: (hasValidation: boolean) => void
  shouldShowChangesInForm?: boolean
}

export type CommandDataSimulationProps<
  S,
  C extends AnyCommandVersion['commandData'] = AnyCommandVersion['commandData'],
> = {
  simulation: Optional<S>
  commandData: C
  simulationType: (
    | 'all'
    | 'allSavings'
    | 'allRisk'
    | 'activeSavings'
    | 'activeRisk'
    | 'risk'
    | 'savings'
  )[]
}

export type FetchSimulationProps<T extends AnyCommandVersion['commandData']> = {
  commandData: Optional<T>
  deflateCommitmentVersion: boolean // naming deviates from our usual convention to match the backend
}

export type CommandFormAndSimulationProps<
  T extends AnyCommandVersion['commandData'],
  S extends GeneralSimulation,
  E = string,
  F extends VFrameBase = VFrameBase,
> = {
  /** Component containing the form input of the command */
  commandDataForm: ComponentType<CommandDataFormProps<T, E>>
  /** Props for simulation. Optional since not all commands has a simulation */
  simulation?: {
    /** Component for displayin the simulation */
    commandSimulation: ComponentType<CommandDataSimulationProps<S, T>>
    /** Function returning a promise of the simulation result.
     * Input must match the type of the commandForm.
     * Simulation type must match the type of the commandSimulation */
    fetchSimulation: (arg0: FetchSimulationProps<T>) => Promise<Optional<S>>
    /** What area to show simulation on, default set to all */
    simulationType?: CommandDataSimulationProps<
      GeneralSimulation,
      T
    >['simulationType']
    /**
     * By default, `fetchSimulation` is assumed to support the
     * deflateCommitmentVersion` boolean parameter. This can
     * be hidden if the simulation does not actually support it.
     *  */
    shouldHideDeflateToggle?: boolean
  }
  onStatusChanged?: (status: SubmitStatus) => void
  onDirtyChange?: (value: boolean) => void
  submit: CommandSubmissionData<NonNullable<T>> | undefined
  commandActionVariant: CommandActionVariantOrSimulation
  isAllowedToDoCalculation?: boolean
  eventVersionWithCommand?: Optional<
    EventVersionWithCommandType<AnyCommandVersion>
  >
  eventId?: Optional<string>
  festinaComponent: Server
} & Pick<
  CommandDataFormProps<T, E, F>,
  | 'entityId'
  | 'isValidationDisabled'
  | 'fetchFrame'
  | 'onFrameIsLoadingChanged'
  | 'onFormHasValidationChanged'
  | 'onSetSubmitForm'
>

export type Props<
  T extends AnyCommandVersion['commandData'],
  S extends GeneralSimulation,
  E = string,
> = {
  /** Id of the form. Used for the form prop on the submit button to
   * trigger submitting the form */
  formId: string
  onSimulationStatus?: (status: 'none' | 'success' | 'failed') => void
  /** Whether to hide submit errors. Useful when they are already displayed elsewhere. */
  isHidingSubmitErrors?: boolean
  shouldShowChangesInForm?: boolean
} & CommandFormAndSimulationProps<T, S, E>

const isCommandData = (
  data: Optional<AnyCommandVersion>,
): data is CommandData => (data as CommandData)?._type.endsWith('CommandData')

/**
 * **Uses of this component must have `FormStateProvider` in its parents.**
 */
export const CommandDataFormAndSimulation = <
  T extends AnyCommandVersion['commandData'],
  S extends GeneralSimulation,
  E = string,
>({
  commandDataForm: CommandForm,
  simulation,
  formId,
  onStatusChanged,
  onDirtyChange,
  onSimulationStatus,
  submit,
  commandActionVariant,
  isAllowedToDoCalculation = true,
  eventVersionWithCommand,
  eventId,
  entityId,
  isValidationDisabled,
  fetchFrame,
  onFrameIsLoadingChanged,
  onFormHasValidationChanged,
  onSetSubmitForm,
  isHidingSubmitErrors,
  shouldShowChangesInForm,
  festinaComponent,
}: Props<T, S, E>) => {
  const [values, setValues] = useState<T>()
  const [shouldDeflateCommitmentVersion, setShouldDeflateCommitmentVersion] =
    useState(false) // @nkm has confirmed the default value is false in the backend too

  const [submitError, setSubmitError] = useState<Error>()
  const { commandVersion } = eventVersionWithCommand ?? {}
  const commandData = commandVersion?.commandData as T

  const [validationOverrides, setValidationOverrides] = useState<
    ValidationOverride[]
  >(
    isCommandData(commandData)
      ? (commandData?.validationOverrides?.filter(notEmpty) ?? [])
      : [],
  )
  const [isSimulationShown, setIsSimulationShown] = useState<boolean>(false)
  const [simulationData, setSimulationData] = useState<Loadable<S>>()
  const [formErrors, setFormErrors] = useState<boolean>()
  const translate = useTranslate()

  const isAllowedToSimulationAndFetchForFrame =
    shouldShowSimulationAndAllowFetchForFrame({
      commandActionVariant,
      isAllowedToDoCalculation,
      commandVersion,
    })

  const withValidationOverrides = useCallback(
    <V,>(values: V): V =>
      validationOverrides.length ? { ...values, validationOverrides } : values,
    [validationOverrides],
  )

  // If the command has validation overrides, set them in the state before submitting
  const onSubmit = submit?.onSubmit
    ? (values: NonNullable<T>) =>
        submit?.onSubmit(withValidationOverrides(values))
    : undefined

  // If the command has validation overrides, set them in the state before submitting
  const handleFetchSimulation =
    simulation?.fetchSimulation &&
    (async ({
      commandData,
      deflateCommitmentVersion,
    }: FetchSimulationProps<T>) => {
      if (!simulation) {
        return
      }
      setIsSimulationShown(true)
      setSimulationData('loading')

      try {
        const simulationValues = sanitizeFormData(commandData)

        if (!simulationValues) {
          throw new Error('No data available for simulation')
        }

        const data = await simulation.fetchSimulation({
          commandData: withValidationOverrides(simulationValues),
          deflateCommitmentVersion,
        })
        setSimulationData(data)
      } catch (e) {
        setSimulationData(undefined)
        setSubmitError(e)
      }
    })

  useEffect(() => {
    if (simulationData && isSimulationShown) {
      if (simulationData instanceof Error) {
        onSimulationStatus?.('failed')

        return
      }

      if (simulationData !== 'loading') {
        onSimulationStatus?.('success')

        return
      }
    }
    onSimulationStatus?.('none')
  }, [simulationData, onSimulationStatus, isSimulationShown])

  useEffect(() => {
    setIsSimulationShown(false)
  }, [validationOverrides])

  const handleInputChange = (values: T) => {
    setIsSimulationShown(false)
    setValues(values)
  }

  const handleSubmitStatus = (status: SubmitStatus) => {
    if (typeUtils.isError(status)) {
      setSubmitError(status)
    }

    if (onStatusChanged) {
      onStatusChanged(status)
    }
  }

  const [validateFormFunction, setValidateFormFunction] =
    useState<() => Promise<boolean>>()

  const handleSetRevalidateForm = useCallback(
    (func: () => Promise<boolean>) => {
      // Can't set func directly because setState will attempt to invoke callable values.
      setValidateFormFunction(() => func)
    },
    [],
  )

  const handleAddValidationOverride = (newOverride: ValidationOverride) => {
    setValidationOverrides((existingValidationOverrides) => [
      ...existingValidationOverrides,
      newOverride,
    ])
  }

  const handleDeleteValidationOverride = (
    validationOverrideKey: Optional<string>,
  ) => {
    setValidationOverrides(
      validationOverrides.filter(
        (override) => override.validationOverrideKey !== validationOverrideKey,
      ),
    )
  }

  return (
    <Suspense fallback={<CommandPaletteModalSpinner />}>
      <Stack className="grow" gap="spacious">
        <Stack className={cx('flex-col', inputWrapperMinWidthStyles)}>
          {submitError && !isHidingSubmitErrors && (
            <>
              {isValidationError(submitError) ? (
                <ValidationErrorMessageAlert
                  error={submitError.body}
                  validationOverrides={validationOverrides}
                  onAddValidationOverride={handleAddValidationOverride}
                />
              ) : (
                <ErrorMessage
                  // max width is set to prevent the form from getting too wide when there's an alert above it
                  className="max-w-lg"
                  error={submitError}
                />
              )}
            </>
          )}
          <ValidationOverridesTable
            validationOverrides={validationOverrides}
            onDeleteValidationOverride={handleDeleteValidationOverride}
          />
          <DataProvider
            data={commandData}
            eventId={eventId}
            previousEventId={eventVersionWithCommand?.previousEventId}
            applyDate={eventVersionWithCommand?.applyDate}
            expectedApplyDate={commandVersion?.expectedApplyDate}
            shouldSkipFrame={!isAllowedToSimulationAndFetchForFrame}
            commandId={commandVersion?.commandId ?? undefined}
            commandActionVariant={commandActionVariant}
            festinaComponent={festinaComponent}
            isAllowedToSimulationAndFetchForFrame={
              isAllowedToSimulationAndFetchForFrame
            }
          >
            <ExtraCommandData
              eventVersionWithCommand={eventVersionWithCommand}
            />
            <CommandForm
              formId={formId}
              // This differs from the `DataProvider` above,
              // because the commandData prop in Form is used to fetch the frame,
              // while `DataProvider` is also used and takes priority for initial values.
              commandData={
                commandActionVariant === 'create'
                  ? withValidationOverrides({} as Partial<T>)
                  : commandData
              }
              onStatusChanged={handleSubmitStatus}
              onInputChange={handleInputChange}
              onDirtyChange={onDirtyChange}
              onErrorChange={setFormErrors}
              onSetRevalidateForm={handleSetRevalidateForm}
              onSetSubmitForm={onSetSubmitForm}
              onSubmit={onSubmit ?? Promise.resolve}
              shouldFieldsBeDisabled={
                commandActionVariant === 'cancel' ||
                commandActionVariant === 'read' ||
                commandActionVariant === 'approve' ||
                commandActionVariant === 'approvePendingCancellation' ||
                commandActionVariant === 'declinePendingCancellation'
              }
              shouldSkipFrame={!isAllowedToSimulationAndFetchForFrame}
              commandActionVariant={commandActionVariant}
              entityId={entityId}
              isValidationDisabled={isValidationDisabled}
              onFrameIsLoadingChanged={onFrameIsLoadingChanged}
              onFormHasValidationChanged={onFormHasValidationChanged}
              onSubmitError={setSubmitError}
              validationOverrides={validationOverrides}
              shouldShowChangesInForm={shouldShowChangesInForm}
              {...(fetchFrame ? { fetchFrame } : {})} // important to not pass undefined! or it would wipe out the fetcher
            />
          </DataProvider>
        </Stack>
        {isAllowedToSimulationAndFetchForFrame && handleFetchSimulation && (
          <Suspense fallback={<CommandPaletteModalSpinner />}>
            <ErrorBoundary FallbackComponent={LifeErrorFallback}>
              <Stack className={cx('flex-col', inputWrapperMinWidthStyles)}>
                {!simulation.shouldHideDeflateToggle && (
                  <div className="flex justify-end gap-2">
                    <p>
                      {translate('commandModalButton.deflateCommitmentVersion')}
                    </p>
                    <RawSwitch
                      checked={shouldDeflateCommitmentVersion}
                      onChange={async (deflateCommitmentVersion) => {
                        setShouldDeflateCommitmentVersion(
                          deflateCommitmentVersion,
                        )

                        if (isSimulationShown && values) {
                          await handleFetchSimulation({
                            commandData: values,
                            deflateCommitmentVersion,
                          })
                        }
                      }}
                    />
                  </div>
                )}
                {isSimulationShown && values ? (
                  <Simulation<S, T>
                    simulation={simulationData}
                    commandSimulation={simulation.commandSimulation}
                    commandData={values}
                    simulationType={simulation.simulationType ?? ['all']}
                  />
                ) : (
                  <div className="flex grow items-center justify-center">
                    <Button
                      disabled={formErrors}
                      onClick={async () => {
                        setSubmitError(undefined)
                        assertNotEmpty(validateFormFunction)

                        const validationErrors = await validateFormFunction()

                        if (validationErrors) {
                          return
                        }

                        await handleFetchSimulation({
                          commandData: values,
                          deflateCommitmentVersion:
                            shouldDeflateCommitmentVersion,
                        })
                      }}
                    >
                      {translate('commandModalButton.simulate')}
                    </Button>
                  </div>
                )}
              </Stack>
            </ErrorBoundary>
          </Suspense>
        )}
      </Stack>
    </Suspense>
  )
}
